import { useState, useEffect } from 'react';
import api from '../../services/api';
import useCurrentUser from '../../hooks/useCurrentUser';
import { format } from 'date-fns';
import useAgence from '../../hooks/useAgence';

export default function MyRoute({ onClose }) {
    const currentUser = useCurrentUser();
    const [deliveries, setDeliveries] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [myUserIri, setMyUserIri] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedLivreurIri, setSelectedLivreurIri] = useState(null);
    const AGENCE = '12 rue du Mouzon, Laxou, France';
    const [optimizing, setOptimizing] = useState(false);
    const [optimResult, setOptimResult] = useState(null);
    const { agenceActive } = useAgence();

    const getAdresseLivraison = (delivery) => {
        if (delivery.adresseSecondaireChoisie && delivery.patient?.adresseSecondaire) {
            return {
                adresse: delivery.patient.adresseSecondaire,
                ville: delivery.patient.villeSecondaire || delivery.patient.ville,
            };
        }
        return {
            adresse: delivery.patient?.adresse,
            ville: delivery.patient?.ville,
        };
    };

    useEffect(() => {
        const loadMyUser = async () => {
            try {
                const res = await api.get('/users');
                const allUsers = res.data.member || [];
                setUsers(allUsers);
                const me = allUsers.find(u => u.email === currentUser?.email);
                if (me) {
                    setMyUserIri(me['@id']);
                    setSelectedLivreurIri(me['@id']);
                }
            } catch (err) {
                console.error(err);
            }
        };
        loadMyUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedLivreurIri) return;
        loadDeliveries();
// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, selectedLivreurIri]);

    const handleOptimize = async () => {
        if (selected.length < 2) {
            alert('Sélectionne au moins 2 livraisons à optimiser');
            return;
        }
        setOptimizing(true);
        setOptimResult(null);
        try {
            const res = await api.post('/route/optimize', {
                deliveryIds: selected,
            });
            const { orderedIds, duration, distance, skipped } = res.data;

            // Réordonne deliveries selon orderedIds, garde les non-sélectionnées à la fin dans leur ordre d'origine
            const orderedSet = new Set(orderedIds);
            const reordered = [
                ...orderedIds.map(id => deliveries.find(d => d.id === id)).filter(Boolean),
                ...deliveries.filter(d => !orderedSet.has(d.id)),
            ];
            setDeliveries(reordered);
            setOptimResult({ duration, distance, skipped });

            if (skipped?.length > 0) {
                alert(`⚠️ ${skipped.length} livraison(s) ignorée(s) car le patient n'est pas géolocalisé. Géolocalisez-le depuis la fiche patient.`);
            }
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || 'Erreur lors de l\'optimisation');
        } finally {
            setOptimizing(false);
        }

    };

    const loadDeliveries = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/deliveries?date[after]=${date}&date[before]=${date}`);
            const all = res.data.member || [];
            const mine = all
                .filter(d => d.livreur?.['@id'] === selectedLivreurIri)
                .filter(d => d.statutPrep !== 'annule')
                .filter(d => d.patient?.statutPatient !== 'en_pause' && d.patient?.statutPatient !== 'hospitalise')
                .filter(d => d.patient?.statutPatient !== 'ftt')
                .sort((a, b) => (a.heureDebut || '').localeCompare(b.heureDebut || ''));
            setDeliveries(mine);
            setSelected(mine.map(d => d.id));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleDelivery = (id) => {
        setSelected(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const deselectAll = () => setSelected([]);

    const openMaps = () => {
        const selectedDeliveries = deliveries.filter(d => selected.includes(d.id));

        if (selectedDeliveries.length === 0) {
            alert('Sélectionne au moins une livraison');
            return;
        }

        const agenceEncoded = encodeURIComponent(AGENCE);
        const waypoints = selectedDeliveries
            .map(d => {
                const { adresse, ville } = getAdresseLivraison(d);
                return encodeURIComponent(`${adresse}, ${ville}, France`);
            })
            .join('|');

        const optimizedFlag = optimResult ? 'false' : 'true';

        const url = `https://www.google.com/maps/dir/?api=1&origin=${agenceEncoded}&destination=${agenceEncoded}&waypoints=${waypoints}&waypoints_optimized=${optimizedFlag}`;

        window.open(url, '_blank');
    };

    const openWaze = () => {
        const selectedDeliveries = deliveries.filter(d => selected.includes(d.id));

        if (selectedDeliveries.length === 0) {
            alert('Sélectionne au moins une livraison');
            return;
        }

        const premiere = selectedDeliveries[0];
        const { adresse, ville } = getAdresseLivraison(premiere);
        const adresseEncoded = encodeURIComponent(`${adresse}, ${ville}, France`);
        const url = `https://waze.com/ul?q=${adresseEncoded}&navigate=yes`;

        window.open(url, '_blank');
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        }} onClick={onClose}>
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '28px',
                    width: '100%',
                    maxWidth: '500px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{margin: 0, fontSize: '18px', fontWeight: '700'}}>
                        🗺️ Trajet optimisé
                        {selectedLivreurIri && selectedLivreurIri !== myUserIri && (
                            <span style={{fontSize: '13px', fontWeight: '500', color: '#888', marginLeft: '8px'}}>— {users.find(u => u['@id'] === selectedLivreurIri)?.nom}</span>
                        )}
                    </h2>
                    <button onClick={onClose} style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '22px',
                        cursor: 'pointer',
                        color: '#888'
                    }}>×
                    </button>
                </div>

                {/* Date */}
                <div style={{marginBottom: '16px'}}>
                    <label style={labelStyle}>Date</label>
                    <input
                        type="date"
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        style={inputStyle}
                    />
                </div>

                {/* Livreur */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>Livreur</label>
                    <select
                        value={selectedLivreurIri || ''}
                        onChange={e => setSelectedLivreurIri(e.target.value)}
                        style={{
                            ...inputStyle,
                            fontWeight: selectedLivreurIri !== myUserIri ? '700' : '400',
                            color: selectedLivreurIri !== myUserIri ? 'var(--accent-color)' : '#1a1a2e',
                        }}
                    >
                        {users.map(u => (
                            <option key={u['@id']} value={u['@id']}>
                                {u['@id'] === myUserIri ? `${u.nom || u.email} (moi)` : (u.nom || u.email)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Point de départ — fixe pour l'instant */}
                <div style={{
                    marginBottom: '16px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#f8fafc',
                    fontSize: '12px',
                    color: '#888',
                }}>
                    🏢 Départ et retour agence ({agenceActive?.nom || 'agence'})
                </div>

                <button
                    onClick={handleOptimize}
                    disabled={optimizing || selected.length < 2}
                    style={{
                        width: '100%',
                        padding: '10px',
                        marginBottom: '16px',
                        backgroundColor: optimizing ? '#a5b4fc' : 'var(--accent-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: (optimizing || selected.length < 2) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                    }}
                >
                    {optimizing ? '🧠 Optimisation en cours...' : '🧠 Optimiser l\'ordre de passage'}
                </button>

                {optimResult && (
                    <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', backgroundColor: '#f5f3ff', fontSize: '12px', color: '#4f46e5', textAlign: 'center' }}>
                        🕐 Temps estimé : {Math.round(optimResult.duration / 60)} min — 📏 {(optimResult.distance / 1000).toFixed(1)} km
                    </div>
                )}

                {/* Liste livraisons */}
                <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>Chargement...</div>
                    ) : deliveries.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
                            Aucune livraison pour cette période
                        </div>
                    ) : (
                        deliveries.map((d, index) => (
                            <div
                                key={d.id}
                                onClick={() => toggleDelivery(d.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    backgroundColor: selected.includes(d.id) ? '#f5f3ff' : '#f8fafc',
                                    border: `2px solid ${selected.includes(d.id) ? 'var(--accent-color)' : '#e0e0e0'}`,
                                    transition: 'all 0.15s',
                                }}
                            >
                                {/* Numéro */}
                                <div style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: selected.includes(d.id) ? 'var(--accent-color)' : '#e0e0e0',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    flexShrink: 0,
                                }}>
                                    {index + 1}
                                </div>

                                {/* Infos */}
                                <div style={{flex: 1}}>
                                    <div style={{fontSize: '14px', fontWeight: '600', color: '#1a1a2e'}}>
                                        {d.patient?.nom}
                                    </div>
                                    <div style={{fontSize: '12px', color: '#888'}}>
                                        📍 {getAdresseLivraison(d).adresse}, {getAdresseLivraison(d).ville}
                                        {d.adresseSecondaireChoisie && (
                                            <span style={{
                                                marginLeft: '4px',
                                                fontSize: '10px',
                                                color: 'var(--accent-color)',
                                                fontWeight: '600'
                                            }}>(secondaire)</span>
                                        )}
                                    </div>
                                    <div style={{
                                        fontSize: '11px',
                                        marginTop: '2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}><span style={{
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: d.periode === 'matin' ? '#fef9c3' : '#dbeafe',
                                        color: d.periode === 'matin' ? '#854d0e' : '#2563eb',
                                        fontSize: '10px',
                                        fontWeight: '600',}}>{d.periode === 'matin' ? 'Matin' : 'Après-midi'}</span>
                                        {d.consigneHoraire && (
                                            <span style={{
                                                padding: '1px 6px',
                                                borderRadius: '4px',
                                                backgroundColor: '#fef3c7',
                                                color: '#d97706',
                                                fontSize: '10px',
                                                fontWeight: '600',
                                            }}>⏰ {d.consigneHoraire}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Checkbox */}
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '4px',
                                    border: `2px solid ${selected.includes(d.id) ? 'var(--accent-color)' : '#ccc'}`,
                                    backgroundColor: selected.includes(d.id) ? 'var(--accent-color)' : 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                {selected.includes(d.id) && <span style={{ color: 'white', fontSize: '12px' }}>✓</span>}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                {/* Footer */}
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', color: '#888', textAlign: 'center' }}>
                            {selected.length} livraison{selected.length > 1 ? 's' : ''} sélectionnée{selected.length > 1 ? 's' : ''}
                            {selected.length > 1 && ' — trajet optimisé par Google Maps'}
                        </div>
                        {selected.length > 0 && (
                            <button
                                onClick={deselectAll}
                                style={{
                                    padding: '2px 10px',
                                    borderRadius: '20px',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: 'white',
                                    color: '#888',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                ✕ Tout décocher
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={openMaps}
                            disabled={selected.length === 0}
                            style={{
                                flex: 1,
                                padding: '12px',
                                backgroundColor: selected.length === 0 ? '#ccc' : '#16a34a',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: selected.length === 0 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '700',
                            }}
                        >
                            🗺️ Google Maps
                        </button>
                        <button
                            onClick={openWaze}
                            disabled={selected.length !== 1}
                            title={selected.length !== 1 ? 'Waze ne gère qu\'une seule destination : sélectionne une unique livraison' : ''}
                            style={{
                                flex: 1,
                                padding: '12px',
                                backgroundColor: selected.length !== 1 ? '#ccc' : '#38bdf8',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: selected.length !== 1 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '700',
                            }}
                        >
                            🚗 Waze
                        </button>
                    </div>
                    {selected.length !== 1 && (
                        <div style={{ fontSize: '11px', color: '#aaa', textAlign: 'center', marginTop: '8px' }}>
                            💡 Waze nécessite une seule livraison sélectionnée à la fois
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const labelStyle = {display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#444'};
const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box'
};